import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import * as actions from '@/store/slices/role.slice'; // Import your action creators
import * as api from '@/services/role'; // Import your API functions
import { PayloadAction } from '@reduxjs/toolkit';
import { isBoolean } from 'lodash';
import {
  ApiResponse,
  ICreateRole,
  IDeleteRole,
  IUpdateRole,
} from '@/services/role/role.type';

// Define action types
export interface GetUserRoleAction extends PayloadAction<number> {
  type: 'user/getUserRole';
}

export interface GetUsersRoleAction extends PayloadAction<number> {
  type: 'user/getUsersRole';
}

export interface CreateUserRoleAction extends PayloadAction<ICreateRole> {
  type: 'user/createUserRole';
}

export interface UpdateUserRoleAction extends PayloadAction<IUpdateRole> {
  type: 'user/updateUserRole';
}

export interface DeleteUserRoleAction extends PayloadAction<IDeleteRole> {
  type: 'user/deleteUserRole';
}

// Define the shape of the API response for a single provider
export interface SingleUserRoleResponse {
  roleId: string;
  roleName: string;
  createdOn?: string;
  updatedOn?: string;
  // ... other properties
}

function* getUsersRole(action: GetUsersRoleAction): Generator {
  try {
    const response: any = yield call(api.fetchUserRoles as any, action?.payload);
    if (isValidUsersResponse(response?.data)) {
      yield put(actions.getUsersRoleSuccess(response?.data?.data));
    } else {
      yield put(actions.getUsersRoleFailure(response?.data));
    }
  } catch (error: any) {
    yield put(
      actions.getUsersRoleFailure(
        error?.response?.data || { message: error?.message },
      ),
    );
  }
}

// Your saga function
function* getUserRole(action: GetUserRoleAction): Generator {
  try {
    const response: any = yield call(api.fetchUserRole, action?.payload);
    const res = response?.data?.data;

    if (isValidUserResponse(res)) {
      yield put({
        type: 'user/getUserSuccess',
        payload: res as SingleUserRoleResponse,
      });
    } else {
      yield put({ type: 'user/getUserRoleFailure' });
    }
  } catch (error) {
    yield put({ type: 'user/getUserRoleFailure' });
  }
}

function* createUserRole(action: CreateUserRoleAction) {
  try {
    const response: unknown = yield call(api.createUserRole as any, action);
    if (isValidUserResponse(response)) {
      yield put({
        type: 'user/createUserSuccess',
        payload: response as SingleUserRoleResponse,
      });
    } else {
      yield put({
        type: 'user/createUserRoleFailure',
        payload: (response as any)?.data,
      });
    }
  } catch (error: any) {
    yield put({
      type: 'user/createUserRoleFailure',
      payload: error?.response?.data || { message: error?.message },
    });
  }
}

function* updateUserRole(action: UpdateUserRoleAction) {
  try {
    const response: unknown = yield call(
      api.updateUserRole,
      action?.payload?.roleId,
      action?.payload,
    );
    if (isValidUserResponse(response)) {
      yield put({
        type: 'user/updateUserRoleSuccess',
        payload: response as SingleUserRoleResponse,
      });
    } else {
      yield put({ type: 'user/updateUserRoleFailure', payload: response });
    }
  } catch (error: any) {
    yield put({
      type: 'user/updateUserRoleFailure',
      payload: error?.response?.data || { message: error?.message },
    });
  }
}

function* deleteUserRole(action: DeleteUserRoleAction) {
  try {
    const response: unknown = yield call(api.deleteUserRole, action?.payload);
    if (isValidUserResponse(response)) {
      yield put({
        type: 'user/deleteUserRoleSuccess',
        payload: response as SingleUserRoleResponse,
      });
    } else {
      yield put({
        type: 'user/deleteUserRoleFailure',
        payload: (response as any)?.data,
      });
    }
  } catch (error: any) {
    yield put({
      type: 'user/deleteUserRoleFailure',
      payload: error?.response?.data || { message: error?.message },
    });
  }
}

function isValidUserResponse(
  response: unknown,
): response is SingleUserRoleResponse {
  return (
    typeof response === 'object' &&
    response !== null &&
    'roleId' in response &&
    'roleName' in response &&
    'createdOn' in response &&
    'updatedOn' in response
    // Add other properties if present in the API response
  );
}

function isValidUsersResponse(response: ApiResponse): boolean {
  return (
    isBoolean(response.success) &&
    response.message === 'Successfully got list of Roles.'
  );
}

function* roleSaga(): Generator {
  // Watch for the specific action type
  yield takeLatest<GetUsersRoleAction>('user/getUsersRole', getUsersRole);
  yield takeEvery<GetUserRoleAction>('user/getUserRole', getUserRole);
  yield takeEvery<CreateUserRoleAction>('user/createUserRole', createUserRole);
  yield takeEvery<UpdateUserRoleAction>('user/updateUserRole', updateUserRole);
  yield takeEvery<DeleteUserRoleAction>('user/deleteUserRole', deleteUserRole);
}

export default roleSaga;
